import React, { useEffect, useMemo, useState } from "react";
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Stack,
    SvgIcon,
    TextField,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Seo } from "src/components/seo";
import { DataHelper } from "src/services/dataHelper";
import { AddItemToCarts } from "./hooks/addItemToCarts";
import Loader from "src/pages/components/loader";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useMounted } from "src/hooks/use-mounted";
import { paths } from "src/paths";
import { useTranslation } from "react-i18next";
import OrderConfirmationModal from "./hooks/useOrderConfirmation";
import { useCart } from "src/hooks/use-carts";
import axios from "axios";
import { Url } from "src/services/http";

const TOP_NAV_HEIGHT = 64;

// Helper: format as AED currency
function formatCurrency(value) {
    return value?.toLocaleString("en-US", { style: "currency", currency: "AED" }) || "AED 0.00";
}

// Helper: avoid division issues
function safeDivide(numerator, denominator) {
    const n = Number(numerator);
    const d = Number(denominator);
    if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return 0;
    return n / d;
}

export default function OrderSummary() {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loader, setLoader] = useState(false);
    const [coupon, setCoupon] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const location = useLocation();
    const [updatingItemId, setUpdatingItemId] = useState(null);
    const isMounted = useMounted();
    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [addressDialogOpen, setAddressDialogOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [orderNo, setOrderNo] = useState('');
    const { refreshCartCount } = useCart();
    const [status, setStatus] = useState("Processing...");

    // form state for add/edit
    const [addressForm, setAddressForm] = useState({
        ID: null,
        englishName: "",
        mobile: "",
        address1: "",
        address2: "",
        region: "",
        country: "",
        zipCode: "",
        deliveryCharges: 0,
        isDefaultAddress: false,
    });
    const [editingAddress, setEditingAddress] = useState(null);

    const navigate = useNavigate();

    // ---- API: fetch addresses ----
    const parseApiAddresses = (raw) => {
        // DataHelper sometimes returns an array directly, or res.data might be JSON string
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        try {
            // if it's a string JSON representing array/object
            if (typeof raw === "string") {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [parsed];
            }
            // if it's an object that has list/details property
            if (raw.list) return raw.list;
            if (raw.details) return raw.details;
            // fallback: try to convert object values into array
            return Array.isArray(raw) ? raw : [raw];
        } catch (e) {
            console.warn("Could not parse addresses response", e);
            return Array.isArray(raw) ? raw : [raw];
        }
    };

    const getAddress = async () => {
        try {
            setLoader(true);
            const ID = 0; // calling with 0 returns all addresses for user per your SP
            const response = await new DataHelper().getById("CustomerAddresses", ID);
            // response.data might be array or stringified JSON
            const list = parseApiAddresses(response?.data);
            if (isMounted()) {
                setAddresses(list);
            }
            return list;
        } catch (err) {
            console.error("Error fetching addresses:", err);
            toast.error("Failed to load addresses");
            return [];
        } finally {
            setLoader(false);
        }
    };

    // ---- order summary items fetch ----
    const getOrderSummaryData = async () => {
        try {
            setLoader(true);
            const ID = location?.state?.ID;
            if (ID) {
                const response = await new DataHelper().operationsDetailsByOrderStatus("Operations", ID, "MoveToCart");
                if (response?.status === 200) {
                    const apiItems =
                        response.data?.details?.map((d) => ({
                            ID: d.ID,
                            itemId: d.itemId,
                            itemName: d.itemName,
                            selectedSize: d.selectedSize,
                            selectedColor: d.selectedColor,
                            image: d.image,
                            price: safeDivide(d.price, d.qty),
                            lineTotal: safeDivide(d.lineTotal, d.qty),
                            lineNetAmount: safeDivide(d.lineNetAmount, d.qty),
                            qty: Number(d.qty) || 0,
                            discountAmount: safeDivide(d.discountAmount, d.qty),
                            taxAmount: safeDivide(d.taxAmount, d.qty),
                        })) || [];
                    setItems(apiItems);
                }
            }
        } catch (err) {
            console.error("Error fetching order summary:", err);
            toast.error("Failed to load order");
        } finally {
            setLoader(false);
        }
    };

    // ---- load both sequentially on mount ----
    useEffect(() => {
        const loadData = async () => {
            await getOrderSummaryData();
            const addr = await getAddress();
            console.log("addr", addr);
            if (addr && addr.length > 0) setSelectedAddress(addr[0]);
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- totals ----
    const subtotal = useMemo(() => items.reduce((s, it) => s + (it.lineTotal || 0) * (it.qty || 0), 0), [items]);
    const discountAmount = useMemo(() => items.reduce((s, it) => s + ((it.discountAmount || 0) * (it.qty || 0)), 0), [items]);
    const tax = useMemo(() => items.reduce((s, it) => s + ((it.taxAmount || 0) * (it.qty || 0)), 0), [items]);
    const shipping = 0;
    const total = useMemo(() => items.reduce((s, it) => s + ((it.lineNetAmount || 0) * (it.qty || 0)), 0), [items]);

    const updateQty = async (ID, delta, selectedSize, selectedColor) => {
        try {
            if (delta >= 1) {
                setLoader(true);
                setUpdatingItemId(ID); // show loader for this item
                const response = await new DataHelper().getById('Items', ID);
                const itemdata = response?.data;
                const updatedData = {
                    ...itemdata,
                    selectedSize: selectedSize,
                    selectedColor: selectedColor
                };
                await new AddItemToCarts().handleAddToCart(null, updatedData, delta, 'Updated Quantity', 'ItemUpdatedFromBuyNow', 'BuyNow');
                await getOrderSummaryData();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false);
            setUpdatingItemId(null); // hide loader for this item
        }
    };
    const removeItem = async (ID, delta, selectedSize, selectedColor) => {
        const response = await new DataHelper().getById('Items', ID);
        const itemdata = response?.data;
        const updatedData = {
            ...itemdata,
            selectedSize: selectedSize,
            selectedColor: selectedColor
        };
        await new AddItemToCarts().handleAddToCart(null, updatedData, delta, 'Item has been Deleted', "ItemDelete", 'MoveToCart');
        await getOrderSummaryData();
    }
    const generateOrderId = () => {
        const timePart = Date.now().toString().slice(-3); // last 3 digits of timestamp
        const randomPart = Math.floor(Math.random() * 100) // 00–99
          .toString()
          .padStart(2, "0");
      
        return "ORD-" + timePart + randomPart; // always 5 digits
      };
    const handlePay = async (operationHeadId, selectedAddressId) => {
        const value = selectedAddress?.Customer?.Email || "";

        // check if it's a number (phone-like)
        const isNumber = /^\+?[0-9]+$/.test(value);
        
        // decide email + phone properly
        const email = isNumber ? "your@mail.com" : value;

        try {
            sessionStorage.setItem(
                'inv_payment_return_context',
                JSON.stringify({ operationHeadId, selectedAddressId })
            );
        } catch (e) {
            console.warn('Could not store payment return context', e);
        }

        const payload = {
            OrderId: generateOrderId(),
            //  Amount: "1",
            Amount: total,
            Currency: "AED",

            BillingName: selectedAddress?.Customer?.contactInfo || "User",
            BillingEmail: email || "your@mail.com",
            BillingTel: selectedAddress?.Customer?.Mobile || "971500000000",

            RedirectUrl: Url.baseUrl + "/payment/response-proxy",
            CancelUrl: Url.baseUrl + "/payment/response-proxy",


            BillingAddress: selectedAddress?.address1 || "Sharjah",
            BillingCity: selectedAddress?.address2 || "Sharjah",
            BillingState: selectedAddress?.state || "Sharjah",
            BillingCountry: "United Arab Emirates",
            BillingZip: "00000",

            // ✅ ADD SHIPPING
            DeliveryName: selectedAddress?.contactInfo || "User",
            DeliveryAddress: selectedAddress?.address1 || "Sharjah",
            DeliveryCity: selectedAddress?.address2 || "Sharjah",
            DeliveryState: selectedAddress?.state || "Sharjah",
            DeliveryCountry: "United Arab Emirates",
            DeliveryZip: "00000",
            DeliveryTel: selectedAddress?.mobile || "971500000000",

        };

        const res = await axios.post(Url.baseUrl + "/payment/initiate", payload);

        const { encRequest, accessCode } = res.data;
        console.log("encRequest", encRequest);
        const form = document.createElement("form");
        form.method = "POST";
        form.action =
            "https://secure.ccavenue.ae/transaction/transaction.do?command=initiateTransaction";
        form.innerHTML = `
            <input type="hidden" name="encRequest" value="${encRequest}" />
            <input type="hidden" name="access_code" value="${accessCode}" />
        `;

        document.body.appendChild(form);
        form.submit();
    };

    const handleConfirmOrder = async () => {
        if (!items || items.length === 0) {
            toast.error("No items in order");
            return;
        }

        const operationHeadId = location?.state?.ID;
        if (!operationHeadId) {
            console.error("operationHeadId not found in location state");
            toast.error("Order information is missing");
            return;
        }

        if (!selectedAddress || !selectedAddress.ID) {
            toast.error("Please select a shipping address");
            return;
        }

        const dataObj = {
            operationHeadId,
            orderStatus: 'Confirm',
            action: 'Completed',
            addressId: selectedAddress.ID
        };

        try {
            setLoader(true);
            await handlePay(operationHeadId, selectedAddress.ID);
            // const response = await new DataHelper().save('Operations', dataObj);
            // console.log("response", response)
            // if (response?.status === 200 || response?.statusCode === 200) {
            //     const id = response?.data?.operationHeadId || response?.operationHeadId;
            //     if (id) {
            //         // await handlePay();
            //         setOrderNo(`${response?.data?.invoiceNo}`);
            //         setOpen(true);
            //         toast.success(t("Order confirmed"));
            //         toast.success(t(status));
            //         refreshCartCount();
            //     }

            // } else {
            //     toast.error("Failed to place order");
            // }

            //   console.log("Save result:", JSON.stringify(response));
        } catch (err) {
            console.error("Checkout failed:", err);
            toast.error("Something went wrong while placing the order");
        } finally {
            setLoader(false);
        }
    };
    // ---- coupon ----
    const applyCoupon = () => {
        const code = coupon.trim().toUpperCase();
        if (code === "SAVE10") setAppliedCoupon({ code: "SAVE10", label: "10% off (max AED 10)" });
        else if (code === "FREESHIP") setAppliedCoupon({ code: "FREESHIP", label: "Free shipping" });
        else {
            setAppliedCoupon(null);
            toast.error("Invalid coupon code");
        }
    };
    const clearCoupon = () => {
        setAppliedCoupon(null);
        setCoupon("");
    };

    // ---- address handlers ----
    const handleSelectAddress = (addr) => {
        setSelectedAddress(addr);
        setAddressDialogOpen(false);
    };

    const openAddAddress = () => {
        setEditingAddress(null);
        setAddressForm({
            ID: null,
            englishName: "",
            mobile: "",
            address1: "",
            address2: "",
            region: "",
            country: "",
            zipCode: "",
            deliveryCharges: 0,
            isDefaultAddress: false,
        });
        setAddressDialogOpen(true);
    };

    const handleEditClick = (addr) => {
        setEditingAddress(addr);
        setAddressForm({
            ID: addr.ID ?? addr.id ?? null,
            englishName: addr.englishName ?? addr.EnglishName ?? addr.name ?? "",
            mobile: addr.mobile ?? addr.Mobile ?? addr.phone ?? "",
            address1: addr.address1 ?? addr.Address1 ?? "",
            address2: addr.address2 ?? addr.Address2 ?? "",
            region: addr.region ?? addr.Region ?? "",
            country: addr.country ?? addr.Country ?? "",
            zipCode: addr.zipCode ?? addr.ZipCode ?? "",
            deliveryCharges: addr.deliveryCharges ?? addr.DeliveryCharges ?? 0,
            isDefaultAddress: !!addr.isDefaultAddress,
        });
        setAddressDialogOpen(true);
    };

    const handleFormChange = (field, value) => {
        setAddressForm((p) => ({ ...p, [field]: value }));
    };

    const handleAddNewAddress = async () => {
        try {
            // validation
            if (!addressForm.englishName || !addressForm.mobile || !addressForm.address1) {
                return toast.error("Please fill required fields");
            }
            setLoader(true);

            const obj = {
                englishName: addressForm.englishName,
                arabicName: addressForm.englishName,
                code: addressForm.code ?? "",
                mobile: addressForm.mobile,
                country: addressForm.country,
                region: addressForm.region,
                address1: addressForm.address1,
                address2: addressForm.address2,
                zipCode: addressForm.zipCode,
                deliveryCharges: addressForm.deliveryCharges,
                // include any other expected properties by backend
            };

            const res = await new DataHelper().save("CustomerAddresses", obj);

            if (res?.status === 200) {
                toast.success("Address added successfully");
                const updated = await getAddress();
                if (updated && updated.length > 0) setSelectedAddress(updated[updated.length - 1]);
                setAddressDialogOpen(false);
            } else {
                console.error("Add address response:", res);
                toast.error("Failed to add address");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to add address");
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateAddress = async () => {
        try {
            if (!addressForm.ID) return toast.error("Missing address ID");
            setLoader(true);

            const obj = {
                ID: addressForm.ID,
                englishName: addressForm.englishName,
                arabicName: addressForm.englishName,
                code: addressForm.code ?? "",
                mobile: addressForm.mobile,
                country: addressForm.country,
                region: addressForm.region,
                address1: addressForm.address1,
                address2: addressForm.address2,
                zipCode: addressForm.zipCode,
                deliveryCharges: addressForm.deliveryCharges,
            };

            const res = await new DataHelper().save("CustomerAddresses", obj); // save should handle update by ID

            if (res?.status === 200) {
                toast.success("Address updated");
                const updated = await getAddress();
                if (updated) {
                    const found = updated.find((x) => (x.ID ?? x.id) === addressForm.ID);
                    if (found) setSelectedAddress(found);
                }
                setEditingAddress(null);
                setAddressDialogOpen(false);
            } else {
                console.error("Update address response:", res);
                toast.error("Failed to update address");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to update address");
        } finally {
            setLoader(false);
        }
    };

    const handleDeleteAddress = async (ID) => {
        try {
            if (!window.confirm("Delete this address?")) return;
            setLoader(true);

            let res;
            if (typeof new DataHelper().delete === "function") {
                const dataObj = { ID: ID };
                res = await new DataHelper().delete("CustomerAddresses", dataObj);
            } else {
                // fallback: send object with ID and tell backend to delete (depends on backend)
                res = await new DataHelper().save("CustomerAddresses", { ID, isDeleted: true });
            }

            if (res?.status === 200) {
                toast.success("Address removed");
                const updated = await getAddress();
                if (updated && updated.length > 0) setSelectedAddress(updated[0]);
                else setSelectedAddress(null);
            } else {
                console.error("Delete response:", res);
                toast.error("Failed to remove address");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to remove address");
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            <Seo title={t("Order Summary")} />
            <Box
                component="main"
                sx={{
                    boxSizing: 'border-box',
                    flexGrow: 1,
                    maxWidth: '100%',
                    overflowX: 'hidden',
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    pt: { xs: 1, sm: 1.5 },
                    pb: { xs: 3, sm: 4 },
                    width: '100%'
                }}
            >
                <Stack spacing={{ xs: 2, md: 2.5 }} sx={{ width: '100%' }}>
                    {/* Page header */}
                    <Stack
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={1.5}
                    >
                        <Stack alignItems="flex-start" direction="row" spacing={1.5}>
                            <IconButton
                                aria-label="back"
                                onClick={() => navigate(-1)}
                                sx={{
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    mt: 0.25
                                }}
                            >
                                <SvgIcon fontSize="small">
                                    <ArrowLeftIcon />
                                </SvgIcon>
                            </IconButton>
                            <Box>
                                <Typography
                                    sx={{ fontSize: { xs: '1.35rem', md: '1.75rem' }, fontWeight: 700, lineHeight: 1.2, m: 0 }}
                                    variant="h5"
                                >
                                    {t("Order Summary")}
                                </Typography>
                                <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
                                    {t("Review items and confirm your order")}
                                </Typography>
                            </Box>
                        </Stack>
                        <Chip
                            label={`${items.length} ${items.length === 1 ? t('item') : t('items')}`}
                            sx={{ fontWeight: 600, height: 32 }}
                            variant="outlined"
                        />
                    </Stack>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: { xs: 2, md: 2.5 },
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(0, 1fr)' },
                            width: '100%'
                        }}
                    >
                        {/* LEFT SIDE */}
                        <Box sx={{ minWidth: 0, width: '100%' }}>
                            {/* Address */}
                            <Card
                                elevation={0}
                                sx={{
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2.5,
                                    mb: { xs: 2, md: 2.5 },
                                    p: { xs: 1.5, sm: 2 }
                                }}
                            >
                                <Stack alignItems={{ xs: 'stretch', sm: 'center' }} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Typography sx={{ alignItems: 'center', display: 'flex', fontWeight: 600, gap: 1 }} variant="subtitle1">
                                        <HomeIcon fontSize="small" /> {t("Shipping Address")}
                                    </Typography>
                                    <Button
                                        onClick={openAddAddress}
                                        size="small"
                                        sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, borderRadius: 2, textTransform: 'none' }}
                                        variant="outlined"
                                    >
                                        {addresses.length > 0 ? t('Change Address') : t('Add Address')}
                                    </Button>
                                </Stack>

                                {selectedAddress ? (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'neutral.900' : 'neutral.50',
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                            borderRadius: 2,
                                            mt: 1.5,
                                            p: 2
                                        }}
                                    >
                                        <Typography fontWeight={600} variant="subtitle1">
                                            {selectedAddress.englishName}
                                        </Typography>
                                        <Typography color="text.secondary" variant="body2">{selectedAddress.address1}</Typography>
                                        {selectedAddress.address2 && (
                                            <Typography color="text.secondary" variant="body2">{selectedAddress.address2}</Typography>
                                        )}
                                        {selectedAddress.region && (
                                            <Typography color="text.secondary" variant="body2">{selectedAddress.region}</Typography>
                                        )}
                                        <Typography sx={{ mt: 0.5 }} variant="body2">{selectedAddress.mobile}</Typography>
                                    </Paper>
                                ) : (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            bgcolor: 'background.default',
                                            border: (theme) => `1px dashed ${theme.palette.divider}`,
                                            borderRadius: 2,
                                            mt: 1.5,
                                            p: 2,
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography color="text.secondary" variant="body2">
                                            {t("No shipping address selected")}.
                                        </Typography>
                                    </Paper>
                                )}
                            </Card>

                            {/* Items */}
                            <Card
                                elevation={0}
                                sx={{
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2.5,
                                    overflow: 'hidden'
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
                                    <Typography sx={{ fontWeight: 600, mb: 1.5 }} variant="subtitle1">
                                        {t("Order Items")}
                                    </Typography>
                                    <List disablePadding>
                                            {items.length === 0 && (
                                                <ListItem>
                                                    <ListItemText primary={t("Your order is empty.")} />
                                                </ListItem>
                                            )}

                                            {items.map((item) => {
                                                const key = item.ID ?? item.id ?? item.itemId;
                                                return (
                                                    <React.Fragment key={key}>
                                                        <ListItem
                                                            alignItems="flex-start"
                                                            disablePadding
                                                            sx={{
                                                                flexDirection: 'column',
                                                                mb: 1.5,
                                                                py: 0
                                                            }}
                                                        >
                                                            <Stack
                                                                direction={{ xs: 'column', md: 'row' }}
                                                                spacing={1.5}
                                                                sx={{
                                                                    alignItems: { xs: 'stretch', md: 'center' },
                                                                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'neutral.900' : 'neutral.50',
                                                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                                                    borderRadius: 2,
                                                                    p: { xs: 1.5, sm: 2 },
                                                                    width: '100%'
                                                                }}
                                                            >
                                                                <Stack
                                                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                                    direction={{ xs: 'column', sm: 'row' }}
                                                                    flex={1}
                                                                    spacing={1.5}
                                                                    sx={{ minWidth: 0, width: '100%' }}
                                                                >
                                                                {/* IMAGE */}
                                                                <Box
                                                                    sx={{
                                                                        width: { xs: 64, sm: 72 },
                                                                        height: { xs: 80, sm: 90 },
                                                                        borderRadius: 1.5,
                                                                        overflow: "hidden",
                                                                        border: (theme) => `1px solid ${theme.palette.divider}`,
                                                                        flexShrink: 0,
                                                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.100',
                                                                    }}
                                                                >
                                                                    <Box
                                                                        component="img"
                                                                        src={item.image || "/no-image.png"}
                                                                        alt={item.itemName}
                                                                        sx={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit: "cover",
                                                                            display: "block",
                                                                        }}
                                                                    />
                                                                </Box>

                                                                {/* ITEM INFO */}
                                                                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                                                    {/* 🏷️ Product Name */}
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            fontSize: { xs: "0.95rem", sm: "1rem" },
                                                                            color: "text.primary",
                                                                            mb: 0.3,
                                                                            lineHeight: 1.4,
                                                                        }}
                                                                    >
                                                                        {item.itemName}
                                                                    </Typography>
                                                                    {item.selectedSize && (
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontWeight: 500,
                                                                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                                                                color: "text.primary",
                                                                                mb: 0.3,
                                                                                lineHeight: 1.4,
                                                                            }}
                                                                        >
                                                                            {t("Size")}: {item.selectedSize}
                                                                        </Typography>
                                                                    )}
                                                                    {item.selectedColor && (
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontWeight: 500,
                                                                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                                                                color: "text.primary",
                                                                                mb: 0.3,
                                                                                lineHeight: 1.4,
                                                                            }}
                                                                        >
                                                                            {t("Color")}: {item.selectedColor}
                                                                        </Typography>
                                                                    )}

                                                                    {/* 💰 Price */}
                                                                    <Typography
                                                                        color="primary.main"
                                                                        sx={{ fontWeight: 700, fontSize: { xs: '0.875rem', sm: '0.9375rem' }, mt: 0.5 }}
                                                                        variant="body2"
                                                                    >
                                                                        {formatCurrency(item.lineNetAmount)}
                                                                    </Typography>

                                                                    {/* 🔢 Quantity Controls + Loader */}
                                                                    <Stack
                                                                        alignItems="center"
                                                                        direction="row"
                                                                        mt={1.4}
                                                                        spacing={1}
                                                                        sx={{ flexWrap: 'wrap', width: '100%' }}
                                                                    >
                                                                        {/* ➖ Decrease Button */}
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => updateQty(item.itemId, item.qty - 1, item.selectedSize, item.selectedColor)}
                                                                            sx={(theme) => ({
                                                                                width: 36,
                                                                                height: 36,
                                                                                borderRadius: 2,
                                                                                border: "1px solid",
                                                                                borderColor: theme.palette.divider,
                                                                                color:
                                                                                    theme.palette.mode === "light"
                                                                                        ? theme.palette.text.primary
                                                                                        : theme.palette.grey[200],
                                                                                bgcolor:
                                                                                    theme.palette.mode === "light"
                                                                                        ? theme.palette.background.paper
                                                                                        : theme.palette.background.default,
                                                                                boxShadow:
                                                                                    theme.palette.mode === "light"
                                                                                        ? "0 1px 3px rgba(0,0,0,0.08)"
                                                                                        : "0 1px 3px rgba(0,0,0,0.4)",
                                                                                transition: "all 0.25s ease-in-out",
                                                                                "&:hover": {
                                                                                    bgcolor: theme.palette.primary.main,
                                                                                    color: theme.palette.primary.contrastText,
                                                                                    borderColor: theme.palette.primary.main,
                                                                                    transform: "translateY(-2px)",
                                                                                    boxShadow:
                                                                                        theme.palette.mode === "light"
                                                                                            ? "0 3px 8px rgba(0,0,0,0.15)"
                                                                                            : "0 3px 8px rgba(0,0,0,0.6)",
                                                                                },
                                                                                "&:active": {
                                                                                    transform: "scale(0.96)",
                                                                                },
                                                                            })}
                                                                        >
                                                                            <RemoveIcon fontSize="small" />
                                                                        </IconButton>

                                                                        {/* 🔢 Quantity Input */}
                                                                        <TextField
                                                                            value={item.qty}
                                                                            size="small"
                                                                            inputProps={{
                                                                                style: {
                                                                                    textAlign: "center",
                                                                                    width: 46,
                                                                                    fontWeight: 600,
                                                                                    fontSize: "0.9rem",
                                                                                },
                                                                            }}
                                                                            sx={(theme) => ({
                                                                                "& .MuiOutlinedInput-root": {
                                                                                    borderRadius: 2,
                                                                                    height: 38,
                                                                                    bgcolor:
                                                                                        theme.palette.mode === "light"
                                                                                            ? "#fff"
                                                                                            : theme.palette.background.default,
                                                                                    "& fieldset": {
                                                                                        borderColor: theme.palette.divider,
                                                                                    },
                                                                                    "&:hover fieldset": {
                                                                                        borderColor: theme.palette.text.secondary,
                                                                                    },
                                                                                    "&.Mui-focused fieldset": {
                                                                                        borderColor: theme.palette.primary.main,
                                                                                        boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
                                                                                    },
                                                                                },
                                                                                "& input": { p: 0 },
                                                                                transition: "all 0.2s ease-in-out",
                                                                            })}
                                                                        />

                                                                        {/* ➕ Increase Button */}
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => updateQty(item.itemId, item.qty + 1, item.selectedSize, item.selectedColor)}
                                                                            sx={(theme) => ({
                                                                                width: 36,
                                                                                height: 36,
                                                                                borderRadius: 2,
                                                                                border: "1px solid",
                                                                                borderColor: theme.palette.divider,
                                                                                color:
                                                                                    theme.palette.mode === "light"
                                                                                        ? theme.palette.text.primary
                                                                                        : theme.palette.grey[200],
                                                                                bgcolor:
                                                                                    theme.palette.mode === "light"
                                                                                        ? theme.palette.background.paper
                                                                                        : theme.palette.background.default,
                                                                                boxShadow:
                                                                                    theme.palette.mode === "light"
                                                                                        ? "0 1px 3px rgba(0,0,0,0.08)"
                                                                                        : "0 1px 3px rgba(0,0,0,0.4)",
                                                                                transition: "all 0.25s ease-in-out",
                                                                                "&:hover": {
                                                                                    bgcolor: theme.palette.primary.main,
                                                                                    color: theme.palette.primary.contrastText,
                                                                                    borderColor: theme.palette.primary.main,
                                                                                    transform: "translateY(-2px)",
                                                                                    boxShadow:
                                                                                        theme.palette.mode === "light"
                                                                                            ? "0 3px 8px rgba(0,0,0,0.15)"
                                                                                            : "0 3px 8px rgba(0,0,0,0.6)",
                                                                                },
                                                                                "&:active": {
                                                                                    transform: "scale(0.96)",
                                                                                },
                                                                            })}
                                                                        >
                                                                            <AddIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Stack>

                                                                </Box>
                                                                </Stack>

                                                                {/* DELETE */}
                                                                <Box sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                                                                    <Button
                                                                        color="error"
                                                                        onClick={() => removeItem(item.itemId, item.qty, item.selectedSize, item.selectedColor)}
                                                                        size="small"
                                                                        startIcon={<DeleteIcon />}
                                                                        sx={{ borderRadius: 2, display: { xs: 'none', sm: 'inline-flex' }, textTransform: 'none' }}
                                                                        variant="outlined"
                                                                    >
                                                                        {t("Remove")}
                                                                    </Button>
                                                                    <IconButton
                                                                        color="error"
                                                                        onClick={() => removeItem(item.itemId, item.qty, item.selectedSize, item.selectedColor)}
                                                                        size="small"
                                                                        sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </Stack>
                                                        </ListItem>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </List>

                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 2.5 }}>
                                            <TextField
                                                fullWidth
                                                label={t("Coupon code")}
                                                onChange={(e) => setCoupon(e.target.value)}
                                                size="small"
                                                value={coupon}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Button disabled={!coupon.trim()} onClick={applyCoupon} size="small" sx={{ textTransform: 'none' }}>
                                                                {t("Apply")}
                                                            </Button>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            />

                                            {appliedCoupon && (
                                                <Chip
                                                    label={`${t("Applied")}: ${appliedCoupon.label}`}
                                                    onDelete={clearCoupon}
                                                    sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, fontWeight: 500 }}
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                        </Box>

                        {/* RIGHT SIDE (sticky on desktop) */}
                        <Box sx={{ minWidth: 0, width: '100%' }}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: (theme) => `1px solid ${theme.palette.divider}`,
                                        borderRadius: 2.5,
                                        position: { lg: 'sticky' },
                                        top: { lg: TOP_NAV_HEIGHT + 16 }
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                                        <Typography sx={{ fontWeight: 700 }} variant="h6">
                                            {t("Price Details")}
                                        </Typography>
                                        <Stack spacing={1.25} sx={{ mt: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography color="text.secondary" variant="body2">{t("Subtotal")}</Typography>
                                                <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography color="text.secondary" variant="body2">{t("Discount")}</Typography>
                                                <Typography color="success.main" variant="body2">-{formatCurrency(discountAmount)}</Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography color="text.secondary" variant="body2">{t("Shipping")}</Typography>
                                                <Typography variant="body2">{shipping === 0 ? t("Free") : formatCurrency(shipping)}</Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography color="text.secondary" variant="body2">{t("Tax")}</Typography>
                                                <Typography variant="body2">{formatCurrency(tax)}</Typography>
                                            </Box>

                                            <Divider sx={{ my: 0.5 }} />

                                            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
                                                    {t("Total")}
                                                </Typography>
                                                <Typography color="primary.main" sx={{ fontWeight: 700 }} variant="h6">
                                                    {formatCurrency(total)}
                                                </Typography>
                                            </Box>

                                            <Button
                                                disabled={items.length === 0 || loader}
                                                fullWidth
                                                onClick={handleConfirmOrder}
                                                size="large"
                                                startIcon={<ShoppingCartCheckoutIcon />}
                                                sx={{ borderRadius: 2, fontWeight: 600, mt: 2, py: 1.25, textTransform: 'none' }}
                                                variant="contained"
                                            >
                                                {t("Confirm Order")}
                                            </Button>

                                            <Button
                                                fullWidth
                                                onClick={() => alert("Estimate shipping clicked")}
                                                size="medium"
                                                startIcon={<LocalShippingIcon />}
                                                sx={{ borderRadius: 2, textTransform: 'none' }}
                                                variant="outlined"
                                            >
                                                {t("Estimate Shipping")}
                                            </Button>

                                            <OrderConfirmationModal
                                                handleClose={() => {
                                                    setOpen(false);
                                                    navigate(paths.dashboard.index);
                                                }}
                                                onViewOrder={() => navigate(paths.profile.myOrder)}
                                                open={open}
                                                orderId={orderNo}
                                            />
                                        </Stack>
                                    </CardContent>
                                </Card>
                        </Box>
                    </Box>
                </Stack>
            </Box>

            {/* Address Dialog (Add / Edit) */}
            <Dialog
                fullWidth
                maxWidth="sm"
                onClose={() => { setAddressDialogOpen(false); setEditingAddress(null); }}
                open={addressDialogOpen}
                PaperProps={{ sx: { borderRadius: 2.5 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>{t("Change Address")}</DialogTitle>
                <DialogContent dividers>
                    <List disablePadding>
                        {addresses.map((addr) => (
                            <ListItem
                                key={addr.ID ?? addr.id}
                                secondaryAction={(
                                    <Stack direction="row" spacing={0.5}>
                                        <IconButton edge="end" onClick={() => handleEditClick(addr)} title="Edit">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton color="error" edge="end" onClick={() => handleDeleteAddress(addr.ID ?? addr.id)} title="Delete">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                )}
                                sx={{
                                    bgcolor: selectedAddress?.ID === addr.ID ? 'action.selected' : 'transparent',
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    mb: 1,
                                    pr: 10
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                                        <LocationOnIcon fontSize="small" />
                                    </Avatar>
                                </ListItemAvatar>

                                <ListItemText
                                    onClick={() => {
                                        handleSelectAddress(addr);
                                        setAddressDialogOpen(false);
                                    }}
                                    primary={addr.englishName ?? addr.address1 ?? addr.name}
                                    secondary={`${addr.address1 ?? ""}${addr.region ? `, ${addr.region}` : ""}${addr.country ? `, ${addr.country}` : ""}`}
                                    sx={{ cursor: 'pointer' }}
                                />
                            </ListItem>
                        ))}
                    </List>

                    <Divider sx={{ my: 2 }} />

                    <Typography gutterBottom sx={{ fontWeight: 600 }} variant="subtitle1">
                        {editingAddress ? t("Edit address") : t("Add new address")}
                    </Typography>

                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <TextField label={t("Name")} onChange={(e) => handleFormChange("englishName", e.target.value)} size="small" value={addressForm.englishName} />
                        <TextField label={t("Phone")} onChange={(e) => handleFormChange("mobile", e.target.value)} size="small" value={addressForm.mobile} />
                        <TextField label={t("Address Line 1")} onChange={(e) => handleFormChange("address1", e.target.value)} size="small" value={addressForm.address1} />
                        <TextField label={t("Address Line 2")} onChange={(e) => handleFormChange("address2", e.target.value)} size="small" value={addressForm.address2} />
                        <TextField label={t("City / Region")} onChange={(e) => handleFormChange("region", e.target.value)} size="small" value={addressForm.region} />
                        <TextField label={t("Country")} onChange={(e) => handleFormChange("country", e.target.value)} size="small" value={addressForm.country} />
                        <TextField label={t("Zip Code")} onChange={(e) => handleFormChange("zipCode", e.target.value)} size="small" value={addressForm.zipCode} />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 1 }}>
                            <Button
                                onClick={() => {
                                    if (editingAddress) handleUpdateAddress();
                                    else handleAddNewAddress();
                                }}
                                sx={{ borderRadius: 2, textTransform: 'none' }}
                                variant="contained"
                            >
                                {editingAddress ? t("Update Address") : t("Add Address")}
                            </Button>

                            <Button
                                onClick={() => {
                                    setAddressDialogOpen(false);
                                    setEditingAddress(null);
                                }}
                                sx={{ borderRadius: 2, textTransform: 'none' }}
                                variant="outlined"
                            >
                                {t("Cancel")}
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions />
            </Dialog>

            {loader && <Loader message={t('Please wait...')} />}
        </>
    );
}
